/**
 * Reload a stale tab once, and never let it loop.
 *
 * After a deploy the previous build's chunk files are gone, so the next lazy
 * view a stale tab opens fails to download. The cure is a reload, which
 * fetches the fresh page. But if a cached page (browser, proxy, anything)
 * makes the reload land back in the same stale build, curing it again would
 * reload forever. This happened: a document response with a day-long cache
 * header turned the self-heal into an infinite reload loop.
 *
 * So the heal is allowed once per minute per tab. A second chunk failure
 * inside that window stays on the page and lets the click fail visibly,
 * which is annoying but honest, instead of a tab that flickers forever.
 */

const KEY = 'wm-heal-at';

/** True if a reload was actually issued. */
export function healStaleBuild(target?: string): boolean {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(KEY)) || 0;
  } catch {
    // Storage can be blocked; the guard degrades to always allowing the
    // reload, which is exactly the old behaviour.
  }
  if (Date.now() - last < 60_000) return false;
  try {
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch {
    // Same as above.
  }
  if (target && target.startsWith('/')) {
    /*
     * The query is the cure. A plain navigation is allowed to serve the
     * document straight from the browser cache, which on a poisoned tab is
     * the very page being escaped. A never-seen URL cannot be cached yet,
     * so it must go to the server. main.ts strips the marker after boot.
     */
    const bust = `${target.includes('?') ? '&' : '?'}fresh=${Date.now()}`;
    window.location.href = target + bust;
  } else {
    window.location.reload();
  }
  return true;
}
